import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  Select,
  MenuItem,
  FormHelperText,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment,
  Autocomplete,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SelectProps } from "./TSelect";

const MUISelect: React.FC<SelectProps> = ({
  name,
  control,
  label,
  options,
  rules,
  error,
  helperText,
  required,
  autoFocus,
  isAutocomplete = false,
  disabled = false,
}) => {
  const memoizedOptions = useMemo(() => options, [options]);
  const formContext = useFormContext();
  const trigger = formContext?.trigger ?? (() => { });

  return (
    <FormControl
      fullWidth
      error={error}
      required={required}
      disabled={disabled}
      sx={{
        "& .MuiInputLabel-asterisk": {
          color: "#F44336",
        },
      }}
    >
      <Controller
        name={name}
        control={control}
        defaultValue={isAutocomplete ? null : ""}
        rules={rules}
        render={({ field: { onChange, value, ref } }) =>
          isAutocomplete ? (
            <Autocomplete
              disabled={disabled}
              options={memoizedOptions}
              getOptionLabel={(option) => option.label}
              value={memoizedOptions.find((opt) => opt.value === value) || null}
              onChange={(_, selectedOption) =>
                onChange(selectedOption?.value ?? "")
              }
              isOptionEqualToValue={(option, val) =>
                val ? option.value === val.value : false
              }
              autoHighlight
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={option.value} {...optionProps}>
                    {option.label}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  inputRef={ref}
                  autoFocus={autoFocus}
                  error={error}
                  required={required}
                  helperText={helperText}
                  onBlur={() => trigger(name)}
                  disabled={disabled}
                />
              )}
            />
          ) : (
            <>
              <InputLabel id={`${name}-label`}>{label}</InputLabel>
              <Select
                labelId={`${name}-label`}
                label={label}
                value={value}
                onChange={onChange}
                inputRef={ref}
                onBlur={() => trigger(name)}
                autoFocus={autoFocus}
                disabled={disabled}
                endAdornment={
                  value ? (
                    <InputAdornment position="end" sx={{ mr: 3.4 }}>
                      <IconButton
                        onClick={() => onChange("")}
                        edge="end"
                        sx={{ padding: 0.5 }}
                        aria-label="Cancel Selection"
                      >
                        <CloseIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              >
                {memoizedOptions.map((option, index) => (
                  <MenuItem
                    key={index}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {helperText && <FormHelperText>{helperText}</FormHelperText>}
            </>
          )
        }
      />
    </FormControl>
  );
};

export default MUISelect;
